import { useCallback } from "react";
import { API_ENDPOINT } from "@cede/utils";
import { useTelegramContext } from "@/contexts/TelegramContext";
import { useApi } from "@/hooks/core/useApi";
import { useHubUser } from "@/hooks/user/useHubUser";
import { useMutation } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";

/**
 * Hook to handle TMA (Telegram Mini App) social linking
 * This calls the backend endpoint to link the user's Telegram account
 * using the initDataRaw from TMA SDK
 */
export const useTmaSocialLinking = () => {
  const { auth, refetchHubUser } = useHubUser();
  const { initDataRaw } = useTelegramContext();
  const api = useApi();

  const linkTmaSocialMutation = useMutation({
    mutationFn: async (identityToken: string) => {
      if (!identityToken) {
        throw new Error("Valid identity token is required");
      }

      const result = await api.postAuth(auth, API_ENDPOINT.HUB_SOCIAL_OAUTH_LINK_TMA);

      if (!result.success) {
        throw new Error("Failed to link TMA social account");
      }

      return result;
    },
    onSuccess: () => {
      // Refresh user data to get the new social account
      refetchHubUser();
    },
    onError: (error) => {
      enqueueSnackbar(`Failed to link TMA social account: ${error instanceof Error ? error.message : String(error)}`, {
        variant: "error",
      });
    },
  });

  /**
   * Automatically link TMA social account if conditions are met
   * This should be called once after successful authentication
   */
  const autoLinkTmaSocial = useCallback(() => {
    if (!initDataRaw) {
      return;
    }

    // Check if already linking
    if (linkTmaSocialMutation.isPending) {
      return;
    }

    linkTmaSocialMutation.mutate(initDataRaw);
  }, [initDataRaw, linkTmaSocialMutation]);

  return {
    autoLinkTmaSocial,
    isLinking: linkTmaSocialMutation.isPending,
    linkTmaSocialMutation,
  };
};
